import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, CheckSquare, Calendar, FileWarning } from 'lucide-react';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import {
  onboardingApi,
  tasksApi,
  taxFilingApi,
  type OnboardingStateDto,
  type InboxBucketDto,
  type TaxFilingDto,
} from '@solara/api-client';

/**
 * Compact compliance health-check banner for the Home page. Hits four small
 * endpoints in parallel and falls back gracefully if any one fails (the user
 * still sees the others).
 */
export function ComplianceSummary() {
  const { t } = useTranslation('p1');
  const [onboarding, setOnboarding] = useState<OnboardingStateDto | null>(null);
  const [buckets, setBuckets] = useState<InboxBucketDto[]>([]);
  const [filings, setFilings] = useState<TaxFilingDto[]>([]);

  useEffect(() => {
    onboardingApi.getState().then(setOnboarding).catch(() => {});
    tasksApi.buckets().then(setBuckets).catch(() => {});
    taxFilingApi.list().then(setFilings).catch(() => {});
  }, []);

  const overdueTasks = buckets.find((b) => b.category === 'OVERDUE')?.count ?? 0;
  const todayTasks = buckets.find((b) => b.category === 'TODAY')?.count ?? 0;
  const awaitingClient = filings.filter((f) => f.status === 'AWAITING_CLIENT_APPROVAL');
  const nextDeadline = filings
    .filter((f) => new Date(f.dueDate) >= new Date() && f.status !== 'ACCEPTED')
    .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())[0];

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <ShieldCheck className="h-4 w-4" />
            {t('dashCards.onboarding')}
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-2 text-sm">
          {onboarding ? (
            <>
              <div className="flex items-center justify-between">
                <span>{onboarding.status}</span>
                <span className="font-mono text-xs">{onboarding.progressPercent}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-surface-muted">
                <div
                  className="h-full rounded-full bg-brand-500 transition-all"
                  style={{ width: `${onboarding.progressPercent}%` }}
                />
              </div>
              <p className="text-xs text-fg-muted">
                {t('dashCards.currentStep', { step: onboarding.currentStep })}
              </p>
              <Link to="/onboarding" className="text-xs text-brand-700 hover:underline">
                {t('dashCards.continue')}
              </Link>
            </>
          ) : (
            <p className="text-xs text-fg-muted">{t('dashCards.loading')}</p>
          )}
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <CheckSquare className="h-4 w-4" />
            {t('dashCards.myTasks')}
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-1 text-sm">
          <div className="flex items-center justify-between">
            <span>{t('dashCards.overdue')}</span>
            <Badge tone={overdueTasks > 0 ? 'danger' : 'neutral'} className="text-[10px]">
              {overdueTasks}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span>{t('dashCards.today')}</span>
            <Badge tone={todayTasks > 0 ? 'warning' : 'neutral'} className="text-[10px]">
              {todayTasks}
            </Badge>
          </div>
          <Link to="/my-tasks" className="text-xs text-brand-700 hover:underline block pt-1">
            {t('dashCards.open')}
          </Link>
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4" />
            {t('dashCards.nextDeadline')}
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-1 text-sm">
          {nextDeadline ? (
            <>
              <p className="font-medium">
                {nextDeadline.filingType} · {new Date(nextDeadline.dueDate).toLocaleDateString()}
              </p>
              <p className="text-xs text-fg-muted">
                {t('dashCards.netAed', {
                  amount: (nextDeadline.netPayableMinor / 100).toLocaleString(),
                })}
              </p>
              <Link to="/tax/filings" className="text-xs text-brand-700 hover:underline">
                {t('dashCards.filings')}
              </Link>
            </>
          ) : (
            <p className="text-xs text-fg-muted">{t('dashCards.noDeadline')}</p>
          )}
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <FileWarning className="h-4 w-4" />
            {t('dashCards.awaitingApproval')}
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-1 text-sm">
          <p className="text-2xl font-semibold">{awaitingClient.length}</p>
          <p className="text-xs text-fg-muted">{t('dashCards.awaitingApprovalSub')}</p>
          {awaitingClient[0] && (
            <Link
              to={`/tax/filings/${awaitingClient[0].id}`}
              className="text-xs text-brand-700 hover:underline"
            >
              {t('dashCards.openLatest')}
            </Link>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
